import { z } from 'zod';

// =============================================================================
// Platform notices (feed 2) — operator-authored broadcasts ("Gemini degraded",
// "Analytics delayed"). Informational: the admin can't *act*, but needs to *know*
// so they don't burn an hour assuming their setup broke. Authored by platform
// admins; lives in PlatformDO `platform_notice`. Kept in sync with the DO schema
// CHECK constraints.
// =============================================================================

// Severity — same vocabulary as hub alerts (anti banner-blindness): only
// `critical` is a persistent banner; `warn`/`info` are quiet.
export const noticeSeveritySchema = z.enum(['critical', 'warn', 'info']);
export type NoticeSeverity = z.infer<typeof noticeSeveritySchema>;

export const noticeStatusSchema = z.enum(['active', 'resolved']);
export type NoticeStatus = z.infer<typeof noticeStatusSchema>;

// Scope — who sees the broadcast. v1 authors `global` (everyone); `provider:<x>`
// and `service:<x>` are accepted and stored opaquely now, resolved to recipients
// at delivery time (a later slice). The regex is the security boundary: scope is
// interpolated into recipient resolution, so it must be a constrained token.
export const NOTICE_SCOPE_REGEX = /^(global|provider:[a-z0-9_-]{1,40}|service:[a-z0-9_-]{1,40})$/;
export const noticeScopeSchema = z
  .string()
  .regex(NOTICE_SCOPE_REGEX, "scope must be 'global', 'provider:<x>', or 'service:<x>'");

// `link` is rendered as an href in the banner — z.string().url() alone accepts
// `javascript:`, so constrain to http(s) for a safe href.
const noticeLinkSchema = z
  .string()
  .url()
  .refine((u) => /^https?:\/\//i.test(u), { message: 'link must be an http(s) URL' });

// The wire shape. Timestamps are ISO-8601 UTC strings (DO SQLite TEXT — same
// representation end-to-end, per the timestamp convention).
export interface PlatformNotice {
  notice_id: string;
  severity: NoticeSeverity;
  scope: string;
  title: string;
  body: string | null;
  link: string | null;
  status: NoticeStatus;
  starts_at: string;
  ends_at: string | null;
  resolved_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// =============================================================================
// Admin authoring
// =============================================================================

export const createNoticeBody = z.object({
  severity: noticeSeveritySchema.default('info'),
  scope: noticeScopeSchema.default('global'),
  title: z.string().trim().min(1).max(200),
  body: z.string().max(2000).optional(),
  link: noticeLinkSchema.optional(),
  starts_at: z.string().datetime().optional(),
  ends_at: z.string().datetime().optional(),
});
export type CreateNoticeBody = z.infer<typeof createNoticeBody>;

// Partial update. Nullable fields (`body`, `link`, `ends_at`) accept `null` to
// clear; omitted keys are left unchanged.
export const updateNoticeBody = z
  .object({
    severity: noticeSeveritySchema.optional(),
    scope: noticeScopeSchema.optional(),
    title: z.string().trim().min(1).max(200).optional(),
    body: z.string().max(2000).nullable().optional(),
    link: noticeLinkSchema.nullable().optional(),
    starts_at: z.string().datetime().optional(),
    ends_at: z.string().datetime().nullable().optional(),
  })
  .refine((b) => Object.keys(b).length > 0, { message: 'at least one field must be provided' });
export type UpdateNoticeBody = z.infer<typeof updateNoticeBody>;

export const listNoticesQuery = z.object({
  status: noticeStatusSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});

export const noticeIdParamSchema = z.object({ id: z.string().min(1) });

export interface AdminNoticeListResponse {
  data: PlatformNotice[];
  total: number;
}

export interface AdminNoticeResponse {
  data: PlatformNotice;
}

// =============================================================================
// Consumer aggregate (any authenticated user)
// =============================================================================

// The current user's active broadcasts (within their display window, scoped to
// them, minus the ones they've acknowledged). v1 surfaces `global`-scoped notices
// to everyone; provider/service-scoped recipient resolution lands with delivery.
export interface NoticesAggregateResponse {
  notices: PlatformNotice[];
}

export interface AckNoticeResponse {
  acked: true;
}
